import { useState } from 'react';

import { AppIcon, AppIcons, FeaturedIcon } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';

import { Box, Chip, Collapse, SxProps, Theme, Typography } from '@mui/material';

const CAREER_TRACKS_MODAL_STRINGS = appStrings.modules.employer.scoop.track.modal;

type CareerTrackDescription = Readonly<{
  iconName: AppIcons;
  title: string;
  description: string;
  examples: readonly string[];
}>;

const careerTrackDescriptions: CareerTrackDescription[] = [
  {
    iconName: 'GridDotsBlank',
    title: CAREER_TRACKS_MODAL_STRINGS.body.semiSkilled.title,
    description: CAREER_TRACKS_MODAL_STRINGS.body.semiSkilled.description,
    examples: CAREER_TRACKS_MODAL_STRINGS.body.semiSkilled.examples,
  },
  {
    iconName: 'GridDotsLeft',
    title: CAREER_TRACKS_MODAL_STRINGS.body.tradesTechnical.title,
    description: CAREER_TRACKS_MODAL_STRINGS.body.tradesTechnical.description,
    examples: CAREER_TRACKS_MODAL_STRINGS.body.tradesTechnical.examples,
  },
  {
    iconName: 'GridDotsHorizontalCenter',
    title: CAREER_TRACKS_MODAL_STRINGS.body.entrepreneurship.title,
    description: CAREER_TRACKS_MODAL_STRINGS.body.entrepreneurship.description,
    examples: CAREER_TRACKS_MODAL_STRINGS.body.entrepreneurship.examples,
  },
  {
    iconName: 'GridDotsVerticalCenter',
    title: CAREER_TRACKS_MODAL_STRINGS.body.middleTierProfessional.title,
    description: CAREER_TRACKS_MODAL_STRINGS.body.middleTierProfessional.description,
    examples: CAREER_TRACKS_MODAL_STRINGS.body.middleTierProfessional.examples,
  },
  {
    iconName: 'GridDotsTop',
    title: CAREER_TRACKS_MODAL_STRINGS.body.upperTierProfessional.title,
    description: CAREER_TRACKS_MODAL_STRINGS.body.upperTierProfessional.description,
    examples: CAREER_TRACKS_MODAL_STRINGS.body.upperTierProfessional.examples,
  },
  {
    iconName: 'GridDotsOuter',
    title: CAREER_TRACKS_MODAL_STRINGS.body.topTierProfessional.title,
    description: CAREER_TRACKS_MODAL_STRINGS.body.topTierProfessional.description,
    examples: CAREER_TRACKS_MODAL_STRINGS.body.topTierProfessional.examples,
  },
];

export const CareerTracksModalHeaderContent = () => (
  <Box>
    <Typography
      variant="text-lg-semibold"
      color={({ backrs }) => backrs.colors['Colors/Text/text-primary']}
    >
      {CAREER_TRACKS_MODAL_STRINGS.header.title}
    </Typography>

    <Typography variant="text-sm-regular">
      {CAREER_TRACKS_MODAL_STRINGS.header.description}
    </Typography>
  </Box>
);

export const CareerTracksModalBodyContent = () => {
  const [expandedCards, setExpandedCards] = useState<boolean[]>(
    new Array(careerTrackDescriptions.length).fill(false),
  );

  const toggleCard = (index: number) => {
    setExpandedCards((prev) => prev.map((expanded, i) => (i === index ? !expanded : expanded)));
  };

  return (
    <Box sx={bodyContentSx}>
      {careerTrackDescriptions.map((description, index) => (
        <Box sx={individualCardContainerSx} key={index} onClick={() => toggleCard(index)}>
          <Box sx={cardTitleContainerSx}>
            <Box sx={iconTitleSx}>
              <FeaturedIcon
                iconName={description.iconName}
                size="lg"
                color="Colors/Base/White"
                backgroundColor="Brand/500"
                shape="round"
                border={false}
              />
              <Typography sx={titleSx} variant="text-lg-semibold">
                {description.title}
              </Typography>
            </Box>
            {!expandedCards[index] && (
              <Box sx={chevronContainerSx}>
                <AppIcon iconName="ChevronDown" diameter="24px" color="Gray/500" />
              </Box>
            )}
            <Collapse in={expandedCards[index]}>
              <Typography variant="text-sm-regular" component="div" sx={descriptionSx}>
                {description.description}
              </Typography>
            </Collapse>
          </Box>
          <Box sx={examplesOuterContainerSx}>
            <Collapse in={expandedCards[index]}>
              <Typography variant="text-sm-regular" sx={examplesTitleSx}>
                {CAREER_TRACKS_MODAL_STRINGS.examples}
              </Typography>
              <Box sx={examplesContainerSx}>
                {description.examples.map((example, index) => (
                  <Chip key={index} size="sm" sx={badgeSx} label={example} />
                ))}
              </Box>
            </Collapse>
          </Box>
        </Box>
      ))}
    </Box>
  );
};

const cardTitleContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xs'],
  width: '100%',
});

const titleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Gray/700'],
});

const bodyContentSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
});

const descriptionSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});

const iconTitleSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: backrs.spacing['spacing-lg'],
  alignItems: 'center',
});

const individualCardContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: { xs: 'column', sm: 'row' },
  gap: backrs.spacing['spacing-xs'],
  borderRadius: backrs.radius['radius-xl'],
  border: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
  p: backrs.spacing['spacing-xl'],
  cursor: 'pointer',
  position: 'relative',
});

const examplesTitleSx: SxProps<Theme> = ({ backrs, typography }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
  fontWeight: typography.fontWeightBold,
  marginBottom: backrs.spacing['spacing-sm'],
});

const badgeSx: SxProps<Theme> = ({ backrs, typography }) => ({
  height: 'auto',
  backgroundColor: backrs.colors['Colors/Base/White'],
  borderRadius: backrs.radius['radius-sm'],
  border: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
  width: 'fit-content',
  '& .MuiChip-label': {
    ...typography['text-sm-medium'],
    color: backrs.colors['Gray/700'],
    paddingTop: backrs.spacing['spacing-xxs'],
    paddingBottom: backrs.spacing['spacing-xxs'],
    paddingLeft: backrs.spacing['spacing-md'],
    paddingRight: backrs.spacing['spacing-md'],
  },
});

const examplesContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xs'],
});

const chevronContainerSx: SxProps<Theme> = ({ backrs }) => ({
  position: 'absolute',
  right: backrs.spacing['spacing-xl'],
  top: '50%',
  transform: 'translateY(-50%)',
});

const examplesOuterContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: { xs: '100%', sm: '234px' },
  minWidth: '234px',
  marginTop: {
    xs: backrs.spacing['spacing-md'],
    sm: 0,
  },
});
